import castArray from 'lodash/castArray';
import omit from 'lodash/omit';
import { Serializer } from 'miragejs';

import { SEARCH_PARAM, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE } from '@api/constants';
import { capitalize } from '@utils';

export const paginate = (json, request) => {
  const pageNumber = parseInt(request.queryParams[SEARCH_PARAM.PAGE_NUMBER] || DEFAULT_PAGE_NUMBER, 10);
  const pageSize = parseInt(request.queryParams[SEARCH_PARAM.PAGE_SIZE] || DEFAULT_PAGE_SIZE, 10);

  const minIndex = (pageNumber - 1) * pageSize;
  const maxIndex = pageNumber * pageSize;
  const total = json.length;

  return {
    data: json.slice(minIndex, maxIndex),
    total,
    [SEARCH_PARAM.PAGE_NUMBER]: pageNumber,
    [SEARCH_PARAM.PAGE_SIZE]: pageSize,
  };
};

const castModels = (modelOrCollection, predicate) =>
  predicate(modelOrCollection) ? [modelOrCollection] : modelOrCollection.models;

const firstOrAll = (array, returnFirst) => (returnFirst ? array[0] : array);

const getForeignModelAttrKey = (foreignModelName, attrKey) => `${foreignModelName}${capitalize(attrKey)}`;

const getForeignModelAttrValue = (foreignModel, attrKey) => {
  const foreignModelAttr = foreignModel?.attrs[attrKey];

  return foreignModelAttr ?? null;
};

const getForeignModelAttrs = (foreignModelName, attrKeyOrKeys, model) => {
  if (!(foreignModelName in model.associations)) {
    throw new Error(`No such '${foreignModelName}' association in '${model.modelName}' model`);
  }
  const foreignModel = model[foreignModelName];

  const attrKeys = castArray(attrKeyOrKeys);

  return attrKeys.reduce(
    (acc, attrKey) => ({
      ...acc,
      [getForeignModelAttrKey(foreignModelName, attrKey)]: getForeignModelAttrValue(foreignModel, attrKey),
    }),
    {},
  );
};

const omitForeignModelsAttrs = (model, foreignModelAttrs) => omit(model.attrs, Object.keys(foreignModelAttrs));

const getForeignModelsAttrs = (model, foreignModelsAttrs) =>
  Object.entries(foreignModelsAttrs).reduce(
    (acc, [foreignModelName, attrKeys]) => ({
      ...acc,
      ...getForeignModelAttrs(foreignModelName, attrKeys, model),
    }),
    {},
  );

const maybeTransformIdToInt = (shouldTransform, model) => ({
  id: shouldTransform ? Number.parseInt(model.id, 10) : model.id,
});

export const configureWithRelationshipSerializer = ({ includeForeignModelAttrs = {}, idToInt = false } = {}) =>
  Serializer.extend({
    serialize(modelOrCollection) {
      const models = castModels(modelOrCollection, this.isModel);

      const serializedJsons = models.map((model) => ({
        ...omitForeignModelsAttrs(model, includeForeignModelAttrs),
        ...maybeTransformIdToInt(idToInt, model),
        ...getForeignModelsAttrs(model, includeForeignModelAttrs),
      }));

      return firstOrAll(serializedJsons, this.isModel(modelOrCollection));
    },
  });

export function configureVersionedSerializer({ baseSerializer = configureWithRelationshipSerializer() } = {}) {
  return baseSerializer.extend({
    serialize(modelOrCollection, request) {
      const { language = 'en' } = request.params;

      const _baseSerializedJson = baseSerializer.prototype.serialize.apply(this, [modelOrCollection, request]);
      const baseSerializedJsons = castArray(_baseSerializedJson);

      const serializedJsons = baseSerializedJsons.map((json) => ({
        ...omit(json, ['en', 'fr']),
        ...json[language].draftContent,
      }));

      return firstOrAll(serializedJsons, this.isModel(modelOrCollection));
    },
  });
}

export function configurePaginatedSerializer({ baseSerializer = configureWithRelationshipSerializer() } = {}) {
  return baseSerializer.extend({
    serialize(modelOrCollection, request) {
      const json = baseSerializer.prototype.serialize.apply(this, [modelOrCollection, request]);

      return paginate(json, request);
    },
  });
}
